import React from 'react';
import { createRoot, Root } from 'react-dom/client';
import '@xyflow/react/dist/style.css';
import './styles.css';
import { DiagramDesignerApp } from './DiagramDesignerApp';
import {
    SquirrelDiagramDesignerMountOptions,
    SquirrelDiagramDesignerUpdateOptions,
    SquirrelDiagramSnapshot
} from './types';

type DiagramPngExporter = () => Promise<string>;

type MountedDesigner = {
    root: Root;
    options: SquirrelDiagramDesignerMountOptions;
    latestSnapshot: SquirrelDiagramSnapshot | null;
    exportPng: DiagramPngExporter | null;
};

const mountedDesigners = new Map<string, MountedDesigner>();

type StoredDiagramPngExport = {
    mimeType: string;
    base64Data: string;
    createdAt: number;
};

type DiagramPngExportDescriptor = {
    exportId: string;
    mimeType: string;
    totalBase64Length: number;
    chunkSize: number;
    chunkCount: number;
};

const diagramPngChunkSize = 12000;
const storedDiagramPngExports =
    new Map<string, StoredDiagramPngExport>();

function getElement(elementId: string): HTMLElement {
    const element = document.getElementById(elementId);

    if (!element) {
        throw new Error(`Squirrel diagram designer mount element '${elementId}' was not found.`);
    }

    return element;
}

function renderDesigner(elementId: string, designer: MountedDesigner): void {
    designer.root.render(
        <React.StrictMode>
            <DiagramDesignerApp
                options={designer.options}
                onSnapshotChanged={(snapshot) => {
                    designer.latestSnapshot = snapshot;
                }}
                onExporterChanged={(exporter) => {
                    designer.exportPng = exporter;
                }}
            />
        </React.StrictMode>
    );
}

function mount(elementId: string, options: SquirrelDiagramDesignerMountOptions): void {
    const element = getElement(elementId);

    const existing = mountedDesigners.get(elementId);
    if (existing) {
        existing.options = options;
        renderDesigner(elementId, existing);
        return;
    }

    const designer: MountedDesigner = {
        root: createRoot(element),
        options,
        latestSnapshot: null,
        exportPng: null
    };

    mountedDesigners.set(elementId, designer);
    renderDesigner(elementId, designer);
}

function update(elementId: string, options: SquirrelDiagramDesignerUpdateOptions): void {
    const existing = mountedDesigners.get(elementId);

    if (!existing) {
        mount(elementId, options);
        return;
    }

    existing.options = {
        ...existing.options,
        ...options
    };

    renderDesigner(elementId, existing);
}

function getDiagram(elementId: string): SquirrelDiagramSnapshot | null {
    return mountedDesigners.get(elementId)?.latestSnapshot ?? null;
}

function getDiagramJson(elementId: string): string | null {
    const diagram = getDiagram(elementId);
    return diagram ? JSON.stringify(diagram, null, 2) : null;
}

async function exportPng(elementId: string): Promise<string> {
    const designer = mountedDesigners.get(elementId);

    if (!designer?.exportPng) {
        throw new Error('The diagram image exporter is not ready yet.');
    }

    return await designer.exportPng();
}

function delay(milliseconds: number): Promise<void> {
    return new Promise((resolve) => {
        window.setTimeout(resolve, milliseconds);
    });
}

async function waitForExporter(
    elementId: string,
    timeoutMilliseconds = 8000
): Promise<void> {
    const startedAt = Date.now();

    while (Date.now() - startedAt < timeoutMilliseconds) {
        if (mountedDesigners.get(elementId)?.exportPng) {
            return;
        }

        await delay(40);
    }

    throw new Error('The diagram image renderer did not become ready.');
}

function createTemporaryElementId(): string {
    const suffix =
        typeof crypto !== 'undefined' &&
            typeof crypto.randomUUID === 'function'
            ? crypto.randomUUID().replaceAll('-', '')
            : `${Date.now()}${Math.random().toString(16).slice(2)}`;

    return `squirrel-diagram-export-${suffix}`;
}

async function exportDiagramJsonToPng(
    options: SquirrelDiagramDesignerMountOptions
): Promise<string> {
    const elementId = createTemporaryElementId();
    const element = document.createElement('div');

    element.id = elementId;
    element.className = 'squirrel-diagram-host-canvas';
    element.setAttribute('aria-hidden', 'true');

    Object.assign(
        element.style,
        {
            position: 'fixed',
            left: '-100000px',
            top: '0',
            width: '1600px',
            height: '1000px',
            overflow: 'hidden',
            pointerEvents: 'none',
            background: '#ffffff',
            zIndex: '-2147483647'
        });

    document.body.appendChild(element);

    try {
        mount(
            elementId,
            {
                ...options,
                readOnly: true
            });

        await waitForExporter(elementId);
        await delay(80);

        return await exportPng(elementId);
    } finally {
        unmount(elementId);
        element.remove();
    }
}

function dataUrlToBlob(dataUrl: string): Blob {
    const commaIndex = dataUrl.indexOf(',');

    if (commaIndex <= 0 || commaIndex === dataUrl.length - 1) {
        throw new Error('The diagram exporter returned an invalid image data URL.');
    }

    const metadata = dataUrl.slice(0, commaIndex);
    const encodedData = dataUrl.slice(commaIndex + 1);

    const metadataMatch =
        /^data:([^;,]+);base64$/i.exec(metadata);

    if (!metadataMatch) {
        throw new Error('The diagram exporter returned an unsupported image format.');
    }

    const mimeType = metadataMatch[1];
    const binary = window.atob(encodedData);
    const bytes = new Uint8Array(binary.length);

    for (let index = 0; index < binary.length; index++) {
        bytes[index] = binary.charCodeAt(index);
    }

    return new Blob(
        [bytes],
        {
            type: mimeType
        });
}

async function exportDiagramJsonToPngStream(
    options: SquirrelDiagramDesignerMountOptions
): Promise<unknown> {
    const imageDataUrl =
        await exportDiagramJsonToPng(options);

    const imageBlob =
        dataUrlToBlob(imageDataUrl);

    return DotNet.createJSStreamReference(imageBlob);
}


function parseImageDataUrl(
    dataUrl: string
): {
    mimeType: string;
    base64Data: string;
} {
    const commaIndex = dataUrl.indexOf(',');

    if (commaIndex <= 0 || commaIndex === dataUrl.length - 1) {
        throw new Error(
            'The diagram exporter returned an invalid image data URL.'
        );
    }

    const metadata = dataUrl.slice(0, commaIndex);
    const base64Data = dataUrl.slice(commaIndex + 1);

    const metadataMatch =
        /^data:(image\/(?:png|jpeg));base64$/i.exec(metadata);

    if (!metadataMatch) {
        throw new Error(
            'The diagram exporter returned an unsupported image format.'
        );
    }

    return {
        mimeType: metadataMatch[1].toLowerCase(),
        base64Data
    };
}

function removeExpiredDiagramPngExports(): void {
    const cutoff = Date.now() - 5 * 60 * 1000;

    for (const [exportId, storedExport] of
        storedDiagramPngExports.entries()) {
        if (storedExport.createdAt < cutoff) {
            storedDiagramPngExports.delete(exportId);
        }
    }
}

async function beginDiagramJsonPngExport(
    options: SquirrelDiagramDesignerMountOptions
): Promise<DiagramPngExportDescriptor> {
    removeExpiredDiagramPngExports();

    const imageDataUrl =
        await exportDiagramJsonToPng(options);

    const parsed =
        parseImageDataUrl(imageDataUrl);

    const exportId =
        createTemporaryElementId();

    storedDiagramPngExports.set(
        exportId,
        {
            mimeType: parsed.mimeType,
            base64Data: parsed.base64Data,
            createdAt: Date.now()
        }
    );

    return {
        exportId,
        mimeType: parsed.mimeType,
        totalBase64Length: parsed.base64Data.length,
        chunkSize: diagramPngChunkSize,
        chunkCount: Math.ceil(
            parsed.base64Data.length / diagramPngChunkSize
        )
    };
}

function readDiagramPngExportChunk(
    exportId: string,
    chunkIndex: number
): string {
    const storedExport =
        storedDiagramPngExports.get(exportId);

    if (!storedExport) {
        throw new Error(
            'The temporary diagram image export could not be found.'
        );
    }

    if (!Number.isInteger(chunkIndex) || chunkIndex < 0) {
        throw new Error(
            'The requested diagram image chunk is invalid.'
        );
    }

    const start =
        chunkIndex * diagramPngChunkSize;

    if (start >= storedExport.base64Data.length) {
        return '';
    }

    return storedExport.base64Data.slice(
        start,
        start + diagramPngChunkSize
    );
}

function releaseDiagramPngExport(
    exportId: string
): void {
    storedDiagramPngExports.delete(exportId);
}

function unmount(elementId: string): void {
    const existing = mountedDesigners.get(elementId);

    if (!existing) {
        return;
    }

    existing.root.unmount();
    mountedDesigners.delete(elementId);
}

declare global {
    const DotNet: {
        createJSStreamReference: (value: Blob) => unknown;
    };

    interface Window {
        squirrelDiagramDesigner: {
            mount: typeof mount;
            update: typeof update;
            getDiagram: typeof getDiagram;
            getDiagramJson: typeof getDiagramJson;
            exportPng: typeof exportPng;
            exportDiagramJsonToPng: typeof exportDiagramJsonToPng;
            exportDiagramJsonToPngStream: typeof exportDiagramJsonToPngStream;
            beginDiagramJsonPngExport: typeof beginDiagramJsonPngExport;
            readDiagramPngExportChunk: typeof readDiagramPngExportChunk;
            releaseDiagramPngExport: typeof releaseDiagramPngExport;
            unmount: typeof unmount;
        };
    }
}

window.squirrelDiagramDesigner = {
    mount,
    update,
    getDiagram,
    getDiagramJson,
    exportPng,
    exportDiagramJsonToPng,
    exportDiagramJsonToPngStream,
    beginDiagramJsonPngExport,
    readDiagramPngExportChunk,
    releaseDiagramPngExport,
    unmount
};
